import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import { ArrowsOutLineVertical } from "@phosphor-icons/react/ArrowsOutLineVertical";
import { BracketsRound } from "@phosphor-icons/react/BracketsRound";
import { ChartBar } from "@phosphor-icons/react/ChartBar";
import { Code } from "@phosphor-icons/react/Code";
import { CodeBlock } from "@phosphor-icons/react/CodeBlock";
import { Divide } from "@phosphor-icons/react/Divide";
import { Equals } from "@phosphor-icons/react/Equals";
import { Image } from "@phosphor-icons/react/Image";
import { Link } from "@phosphor-icons/react/Link";
import { LinkBreak } from "@phosphor-icons/react/LinkBreak";
import { ListDashes } from "@phosphor-icons/react/ListDashes";
import { ListNumbers } from "@phosphor-icons/react/ListNumbers";
import { Minus } from "@phosphor-icons/react/Minus";
import { Plus } from "@phosphor-icons/react/Plus";
import { Quotes } from "@phosphor-icons/react/Quotes";
import { Scan } from "@phosphor-icons/react/Scan";
import { Table } from "@phosphor-icons/react/Table";
import { TextAlignCenter } from "@phosphor-icons/react/TextAlignCenter";
import { TextAlignJustify } from "@phosphor-icons/react/TextAlignJustify";
import { TextAlignLeft } from "@phosphor-icons/react/TextAlignLeft";
import { TextAlignRight } from "@phosphor-icons/react/TextAlignRight";
import { TextB } from "@phosphor-icons/react/TextB";
import { TextItalic } from "@phosphor-icons/react/TextItalic";
import { TextStrikethrough } from "@phosphor-icons/react/TextStrikethrough";
import { X } from "@phosphor-icons/react/X";
import { Level } from "@tiptap/extension-heading";
import { type Editor } from "@tiptap/react";
import * as React from "react";

import { Option } from "~ui-components/components/atoms/option";
import { useDialog } from "~ui-components/hooks/use-dialog";
import { usePopover } from "~ui-components/hooks/use-popover";

import { ChartDialog, ChartFormValues } from "./chart-menu";
import {
	AggregateAttributeSelect,
	AggregateAttributeSelectProps,
} from "./custom-tools/aggregate-attribute-select";
import {
	AttributeSelect,
	AttributeSelectProps,
} from "./custom-tools/attribute-select";
import { Equation, EquationProps } from "./custom-tools/equation";
import {
	UserInputSelect,
	UserInputSelectProps,
} from "./custom-tools/user-input-select";
import { IFrameDialog, IFrameFormValues } from "./iframe-menu";
import { ImageDialog, ImageFormValues } from "./image-menu";
import { LinkDialog, LinkFormValues } from "./link-menu";

export interface TextEditorToolbarProps {
	editor: Editor;
	toolsProps?: {
		Typography?: boolean;
		Bold?: boolean;
		Italic?: boolean;
		Strike?: boolean;
		BulletList?: boolean;
		OrderedList?: boolean;
		TextAlign?: boolean;
		HorizontalRule?: boolean;
		Blockquote?: boolean;
		CodeBlock?: boolean;
		Code?: boolean;
		Table?: boolean;
		AttributeSelect?: false | Omit<AttributeSelectProps, "editor">;
		Equation?: false | Omit<EquationProps, "editor">;
		UserInputSelect?: false | Omit<UserInputSelectProps, "editor">;
		LinkInput?:
			| false
			| {
					hrefAttributes?: string[];
					textAttributes?: string[];
			  };
		ImageInput?:
			| false
			| {
					srcAttributes?: string[];
			  };
		IFrameInput?:
			| false
			| {
					srcAttributes?: string[];
			  };
		Chart?:
			| false
			| {
					attributes?: string[];
			  };
		MathOperators?: boolean;
		AggregateAttributeSelect?:
			| false
			| Omit<AggregateAttributeSelectProps, "editor">;
	};
}

export const TextEditorToolbar: React.FC<TextEditorToolbarProps> = ({
	editor,
	toolsProps,
}) => {
	const linkDialog = useDialog<LinkFormValues>();
	const imageDialog = useDialog<ImageFormValues>();
	const iframeDialog = useDialog<IFrameFormValues>();
	const chartDialog = useDialog<ChartFormValues>();
	const alignPopover = usePopover<HTMLButtonElement>();

	const isAlignActive = [
		editor.isActive({ textAlign: "left" }),
		editor.isActive({ textAlign: "center" }),
		editor.isActive({ textAlign: "right" }),
		editor.isActive({ textAlign: "justify" }),
	].some(Boolean);

	return (
		<Stack
			className="tiptap-toolbar"
			spacing={1}
			sx={{
				borderBottom: "1px solid var(--mui-palette-divider)",
				p: "8px",
				minHeight: "57px",
			}}>
			{linkDialog.open && !!toolsProps?.LinkInput && (
				<LinkDialog
					{...toolsProps?.LinkInput}
					editor={editor}
					onClose={linkDialog.handleClose}
					initialValues={linkDialog.data}
				/>
			)}
			{imageDialog.open && !!toolsProps?.ImageInput && (
				<ImageDialog
					{...toolsProps?.ImageInput}
					editor={editor}
					onClose={imageDialog.handleClose}
					initialValues={imageDialog.data}
				/>
			)}
			{iframeDialog.open && !!toolsProps?.IFrameInput && (
				<IFrameDialog
					{...toolsProps?.IFrameInput}
					editor={editor}
					onClose={iframeDialog.handleClose}
					initialValues={iframeDialog.data}
				/>
			)}
			{chartDialog.open && !!toolsProps?.Chart && (
				<ChartDialog
					{...toolsProps?.Chart}
					editor={editor}
					onClose={chartDialog.handleClose}
					initialValues={chartDialog.data}
				/>
			)}
			<Stack
				direction="row"
				spacing={0.5}
				sx={{ alignItems: "center", flexWrap: "wrap" }}>
				{!!toolsProps?.MathOperators && (
					<>
						<ToolbarButton
							onClick={() => editor.chain().focus().insertContent(`=`).run()}>
							<Equals />
						</ToolbarButton>
						<ToolbarButton
							onClick={() => editor.chain().focus().insertContent(`()`).run()}>
							<BracketsRound />
						</ToolbarButton>
						<ToolbarButton
							onClick={() => editor.chain().focus().insertContent(`+`).run()}>
							<Plus />
						</ToolbarButton>
						<ToolbarButton
							onClick={() => editor.chain().focus().insertContent(`-`).run()}>
							<Minus />
						</ToolbarButton>
						<ToolbarButton
							onClick={() => editor.chain().focus().insertContent(`x`).run()}>
							<X />
						</ToolbarButton>
						<ToolbarButton
							onClick={() => editor.chain().focus().insertContent(`÷`).run()}>
							<Divide />
						</ToolbarButton>
					</>
				)}
				{!!toolsProps?.Typography && (
					<Select
						onChange={(event: SelectChangeEvent) => {
							const value = event.target.value;

							if (!value) {
								return;
							}

							if (value === "p") {
								editor.chain().focus().setNode("paragraph").run();
								return;
							}

							if (value.startsWith("h")) {
								const level = parseInt(value.replace("h", ""));

								if (!isNaN(level) && level >= 1 && level <= 6) {
									editor
										.chain()
										.focus()
										.setNode("heading", { level: level as Level })
										.run();
								}
							}
						}}
						value={getFontValue(editor)}>
						<Option
							disabled={
								!editor.can().chain().focus().setNode("paragraph").run()
							}
							value="p">
							Paragrah
						</Option>
						{([1, 2, 3, 4, 5, 6] as const).map((level) => (
							<Option
								disabled={
									!editor
										.can()
										.chain()
										.focus()
										.setNode("heading", { level })
										.run()
								}
								key={level}
								value={`h${level}`}>
								Heading {level}
							</Option>
						))}
					</Select>
				)}
				{!!toolsProps?.Bold && (
					<ToolbarButton
						active={editor.isActive("bold")}
						disabled={!editor.can().chain().focus().toggleMark("bold").run()}
						onClick={() => {
							editor.chain().focus().toggleMark("bold").run();
						}}>
						<TextB />
					</ToolbarButton>
				)}
				{!!toolsProps?.Italic && (
					<ToolbarButton
						active={editor.isActive("italic")}
						disabled={!editor.can().chain().focus().toggleMark("italic").run()}
						onClick={() => {
							editor.chain().focus().toggleMark("italic").run();
						}}>
						<TextItalic />
					</ToolbarButton>
				)}
				{!!toolsProps?.Strike && (
					<ToolbarButton
						active={editor.isActive("strike")}
						disabled={!editor.can().chain().focus().toggleMark("strike").run()}
						onClick={() => {
							editor.chain().focus().toggleMark("strike").run();
						}}>
						<TextStrikethrough />
					</ToolbarButton>
				)}
				{!!toolsProps?.BulletList && (
					<ToolbarButton
						active={editor.isActive("bulletList")}
						disabled={
							!editor
								.can()
								.chain()
								.focus()
								.toggleNode("bulletList", "paragraph")
								.run()
						}
						onClick={() => {
							editor
								.chain()
								.focus()
								.toggleNode("bulletList", "paragraph")
								.run();
						}}>
						<ListDashes />
					</ToolbarButton>
				)}
				{!!toolsProps?.OrderedList && (
					<ToolbarButton
						active={editor.isActive("orderedList")}
						disabled={
							!editor
								.can()
								.chain()
								.focus()
								.toggleNode("orderedList", "paragraph")
								.run()
						}
						onClick={() => {
							editor
								.chain()
								.focus()
								.toggleNode("orderedList", "paragraph")
								.run();
						}}>
						<ListNumbers />
					</ToolbarButton>
				)}
				{!!toolsProps?.LinkInput && (
					<>
						<ToolbarButton
							onClick={() => {
								const selectedText = editor.state.doc.textBetween(
									editor.state.selection.from,
									editor.state.selection.to,
								);
								linkDialog.handleOpen({
									text: selectedText,
									href: "",
									openInNewTab: true,
								});
							}}>
							<Link />
						</ToolbarButton>
						<ToolbarButton
							active={editor.isActive("link")}
							disabled={!editor.can().chain().focus().unsetLink().run()}
							onClick={() => {
								editor.chain().focus().unsetLink().run();
							}}>
							<LinkBreak />
						</ToolbarButton>
					</>
				)}
				{!!toolsProps?.AttributeSelect && (
					<AttributeSelect
						{...toolsProps.AttributeSelect}
						editor={editor}
					/>
				)}
				{!!toolsProps?.AggregateAttributeSelect && (
					<AggregateAttributeSelect
						{...toolsProps.AggregateAttributeSelect}
						editor={editor}
					/>
				)}
				{!!toolsProps?.Equation && (
					<Equation
						{...toolsProps.Equation}
						editor={editor}
					/>
				)}
				{!!toolsProps?.UserInputSelect && (
					<UserInputSelect
						{...toolsProps.UserInputSelect}
						editor={editor}
					/>
				)}
				{!!toolsProps?.ImageInput && (
					<ToolbarButton onClick={() => imageDialog.handleOpen()}>
						<Image />
					</ToolbarButton>
				)}
				{!!toolsProps?.TextAlign && (
					<>
						<ToolbarButton
							ref={alignPopover.anchorRef}
							onClick={alignPopover.handleOpen}
							disabled={!isAlignActive}
							active={isAlignActive}>
							{(editor.isActive({ textAlign: "left" }) || !isAlignActive) && (
								<TextAlignLeft />
							)}
							{editor.isActive({ textAlign: "center" }) && <TextAlignCenter />}
							{editor.isActive({ textAlign: "right" }) && <TextAlignRight />}
							{editor.isActive({ textAlign: "justify" }) && (
								<TextAlignJustify />
							)}
						</ToolbarButton>
						<Menu
							anchorEl={alignPopover.anchorRef.current}
							open={alignPopover.open}
							onClose={alignPopover.handleClose}>
							<Stack direction="row">
								<ToolbarButton
									onClick={() =>
										editor.chain().focus().setTextAlign("left").run()
									}
									active={editor.isActive({ textAlign: "left" })}>
									<TextAlignLeft />
								</ToolbarButton>
								<ToolbarButton
									onClick={() =>
										editor.chain().focus().setTextAlign("center").run()
									}
									active={editor.isActive({ textAlign: "center" })}>
									<TextAlignCenter />
								</ToolbarButton>
								<ToolbarButton
									onClick={() =>
										editor.chain().focus().setTextAlign("right").run()
									}
									active={editor.isActive({ textAlign: "right" })}>
									<TextAlignRight />
								</ToolbarButton>
								<ToolbarButton
									onClick={() =>
										editor.chain().focus().setTextAlign("justify").run()
									}
									active={editor.isActive({ textAlign: "justify" })}>
									<TextAlignJustify />
								</ToolbarButton>
							</Stack>
						</Menu>
					</>
				)}
				{!!toolsProps?.HorizontalRule && (
					<ToolbarButton
						onClick={() =>
							editor.chain().focus().setNode("horizontalRule").run()
						}>
						<ArrowsOutLineVertical />
					</ToolbarButton>
				)}
				{!!toolsProps?.Blockquote && (
					<ToolbarButton
						onClick={() =>
							editor.chain().focus().toggleNode("blockquote", "paragraph").run()
						}
						active={editor.isActive("blockquote")}>
						<Quotes />
					</ToolbarButton>
				)}
				{!!toolsProps?.CodeBlock && (
					<ToolbarButton
						onClick={() =>
							editor.chain().focus().toggleNode("codeBlock", "paragraph").run()
						}
						active={editor.isActive("codeBlock")}>
						<CodeBlock />
					</ToolbarButton>
				)}
				{!!toolsProps?.Code && (
					<ToolbarButton
						onClick={() => editor.chain().focus().toggleMark("code").run()}
						active={editor.isActive("code")}>
						<Code />
					</ToolbarButton>
				)}
				{!!toolsProps?.IFrameInput && (
					<ToolbarButton onClick={() => iframeDialog.handleOpen()}>
						<Scan />
					</ToolbarButton>
				)}
				{!!toolsProps?.Table && (
					<ToolbarButton
						onClick={() =>
							editor
								.chain()
								.focus()
								.insertTable({ rows: 3, cols: 3, withHeaderRow: true })
								.run()
						}>
						<Table />
					</ToolbarButton>
				)}
				{!!toolsProps?.Chart && (
					<ToolbarButton onClick={() => chartDialog.handleOpen()}>
						<ChartBar />
					</ToolbarButton>
				)}
			</Stack>
		</Stack>
	);
};

function getFontValue(editor: Editor): string {
	return editor.isActive("paragraph")
		? "p"
		: editor.isActive("heading", { level: 1 })
			? "h1"
			: editor.isActive("heading", { level: 2 })
				? "h2"
				: editor.isActive("heading", { level: 3 })
					? "h3"
					: editor.isActive("heading", { level: 4 })
						? "h4"
						: editor.isActive("heading", { level: 5 })
							? "h5"
							: editor.isActive("heading", { level: 6 })
								? "h6"
								: "p";
}

interface ToolbarButtonProps {
	active?: boolean;
	children: React.ReactNode;
	disabled?: boolean;
	onClick: () => void;
}

const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
	function ToolbarButton({ active, children, disabled, onClick }, ref) {
		return (
			<IconButton
				color={active ? "primary" : "secondary"}
				disabled={disabled}
				onClick={onClick}
				ref={ref}>
				{children}
			</IconButton>
		);
	},
);
